import { BREAKPOINTS, SPACING_2, SPACING_4 } from '@mnd-frontend/ui/shared/tokens';
import debounce from 'lodash-es/debounce';
import {
  Fragment,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import Stepper from '../Stepper';

// #region Styled components
const AnimationTracker = styled.ul<{ $isSliderDisabled: boolean }>`
  width: 100%;
  display: flex;
  overflow-x: auto;
  padding: 0;
  scrollbar-width: none;

  ${({ $isSliderDisabled }) =>
    $isSliderDisabled &&
    css`
      scroll-snap-type: x mandatory;
      justify-content: center;

      @media (min-width: ${BREAKPOINTS.XXL}px) {
         scrollbar-width: thin;
      }
  `}

  &.x-scroll {
    scroll-snap-type: x mandatory; // Does not work with auto scroll
    
    @media (min-width: ${BREAKPOINTS.XXL}px) {
      scrollbar-width: thin;
    }
  }
`;

const StepperContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  width: fit-content;
  margin-top: ${SPACING_4};
`;

const SlideItem = styled.li`
  display: flex;
  gap: ${SPACING_4};
  margin: ${SPACING_2};
  width: fit-content;
  scroll-snap-align: center;
`;
// #endregion

// #region Helpers
export function getNumOfSlides(numOfItems: number, itemsPerSlide: number) {
  if (itemsPerSlide !== 1) {
    return numOfItems % itemsPerSlide === 0
      ? numOfItems / itemsPerSlide
      : numOfItems / itemsPerSlide + 1;
  }

  return numOfItems;
}
/**
 * Create 2D array from item list.
 * @param items {array} - a list of total items
 * @param numOfSlides {number}
 * Example
 *  Arg1: [item1, item2, item3, item4, item5]
 *  Arg2: 3
 *  Return:
 *   [
 *    [item1, item2], // slide 1
 *    [item3, item4], // slide 2
 *    [item5], // slide 3
 *   ]
 */
export function create2DSlideArray<T>(items: T[], numOfSlides: number): T[][] {
  const itemsCopy = [...items];
  const slides: T[][] = [];
  const itemsPerSlide = Math.ceil(itemsCopy.length / numOfSlides);

  while (itemsCopy.length > 0) {
    slides.push(itemsCopy.splice(0, itemsPerSlide));
  }

  return slides;
}
// #endregion

const Slides = ({
  items,
  firstItemRef,
}: {
  items: Array<ReactNode | ReactNode[]> | ReactNode | ReactNode[];
  firstItemRef: RefObject<HTMLLIElement>;
}) => {
  return (
    <>
      {Array.isArray(items) &&
        items?.map((nestedItems, index) => (
          <SlideItem key={index} {...(index === 0 ? { ref: firstItemRef } : {})}>
            {Array.isArray(nestedItems) &&
              nestedItems.map((item, index) => <Fragment key={index}>{item}</Fragment>)}
          </SlideItem>
        ))}
    </>
  );
};

const ANIMATION_INTERVAL = 4000;

const Slider = ({
  itemsLength,
  children,
  isDisabled = false,
}: {
  itemsLength: number;
  children: Array<ReactNode | ReactNode[]> | ReactNode | ReactNode[];
  isDisabled?: boolean;
}) => {
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const firstItemRef = useRef<HTMLLIElement>(null);
  const endBorderRef = useRef<HTMLLIElement>(null);
  const sliderWrapperRef = useRef<HTMLUListElement>(null);

  const [moveForward, setMoveForward] = useState(true);
  const browserDefaultFontSize = useRef(16);

  const controlDirection = useCallback(
    (prevState: number) => {
      if (prevState === 0) {
        setMoveForward(true);
        return prevState + 1;
      }
      if (prevState === itemsLength - 1) {
        setMoveForward(false);
        return prevState - 1;
      }

      if (moveForward) {
        return prevState + 1;
      }

      return prevState - 1;
    },
    [itemsLength, moveForward],
  );

  /**
   * Auto scroll to move a slide back- and forward at a time.
   */
  const autoScroll = useCallback(() => {
    setActiveItemIndex(prev => controlDirection(prev));

    const getXaxisMovement = () => {
      if (activeItemIndex === 0 || (moveForward && activeItemIndex !== itemsLength - 1)) {
        return activeItemIndex + 1;
      }
      return activeItemIndex - 1;
    };

    /**
     * Set browser and DOM dependent sizes
     */
    const cssStyle = getComputedStyle(document.documentElement);
    browserDefaultFontSize.current = +cssStyle.fontSize.split('px')[0];

    const slideDistance =
      (firstItemRef?.current?.getBoundingClientRect().width ?? 0) +
      /* 2rem (side margins) * browser's default font-size */
      browserDefaultFontSize.current * 2;

    sliderWrapperRef?.current?.scrollTo({
      top: 0,
      left: slideDistance * getXaxisMovement(),
      behavior: 'smooth',
    });
  }, [activeItemIndex, moveForward, itemsLength, controlDirection]);

  const handleManualScroll = debounce(() => {
    if (isDisabled) return;

    const firstItemPosition = firstItemRef.current?.getBoundingClientRect();
    const endBorderPosition = endBorderRef.current?.getBoundingClientRect();
    const sliderWrapperRect = sliderWrapperRef.current?.getBoundingClientRect();
    /**
     * Adjust active card index when manually scrolled.
     */
    // When the first item is in the view.
    if ((firstItemPosition?.x ?? 0) > 0) {
      if (activeItemIndex === 0) return;
      setActiveItemIndex(0);
      return;
    }

    // When the last item is in the view.
    if (
      (endBorderPosition?.x ?? 0) ===
      (sliderWrapperRect?.width ?? 0) + (sliderWrapperRect?.x ?? 0)
    ) {
      setActiveItemIndex(itemsLength - 1);
      return;
    }

    // Handle the rest of cases.
    // Calculate how many cards are off from the screen.
    const calcPosition = firstItemPosition
      ? Math.floor(-firstItemPosition.x / firstItemPosition.width)
      : 0;
    // Manually scrolled back to the same item, do nothing.
    if (calcPosition === activeItemIndex) return;
    // Manually scrolled to a different item than the current active item, update active index.
    setActiveItemIndex(calcPosition);
  }, 300);

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // When the section is interacted, auto scroll should be stopped. (a11y & ux)
  const stopAutoScroll = () => {
    if (isDisabled) return;

    if (timer?.current) {
      clearInterval(timer?.current);
      timer.current = null;
    }

    sliderWrapperRef.current?.classList.add('x-scroll');
  };

  const resumeAutoScroll = useCallback(() => {
    if (isDisabled) return;

    sliderWrapperRef.current?.classList.remove('x-scroll');
    timer.current = setInterval(autoScroll, ANIMATION_INTERVAL);
  }, [autoScroll, isDisabled]);

  useEffect(() => {
    if (!isDisabled) {
      timer.current = setInterval(autoScroll, ANIMATION_INTERVAL);
    }

    return () => {
      if (!isDisabled) {
        if (timer.current) {
          clearInterval(timer.current);
          timer.current = null;
        }
      }
    };
  }, [autoScroll, isDisabled]);

  return (
    <>
      <AnimationTracker
        ref={sliderWrapperRef}
        $isSliderDisabled={isDisabled}
        onTouchMove={handleManualScroll}
        onMouseMove={handleManualScroll}
        onMouseOver={stopAutoScroll}
        onTouchStart={stopAutoScroll}
        onMouseLeave={resumeAutoScroll}
        onTouchCancel={resumeAutoScroll}
      >
        <Slides items={children} firstItemRef={firstItemRef} />
        <li ref={endBorderRef} style={{ visibility: 'hidden' }}></li>
      </AnimationTracker>

      <StepperContainer style={{ visibility: isDisabled ? 'hidden' : 'visible' }}>
        <Stepper numOfSteps={itemsLength} activeStepIndex={activeItemIndex} />
      </StepperContainer>
    </>
  );
};
export default Slider;
